'use client';

import { useEffect, useMemo, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';

import { GuideOrbLoading } from '@/components/ui/GuideOrb';
import { StudioAction } from '@/components/ui/StudioAction';
import { CreationGlassBackdrop } from '@/components/visual-editor/CreationGlassBackdrop';
import recovery from '@/components/layout/WorkspaceRecovery.module.css';
import { buildOnboardingRedirect, isOnboardingRequired } from '@/lib/onboarding';
import { isOnboardingProductRoute } from '@/lib/onboarding-product-route';
import { isPublicRoutePath } from '@/lib/routing/public-routes';
import { useAuthStore } from '@/stores/authStore';
import { useUserProfileStore } from '@/stores/userProfileStore';

export function OnboardingGate({ children }: { children: React.ReactNode }) {
  const pathname = usePathname() || '';
  const searchParams = useSearchParams();
  const router = useRouter();
  const t = useTranslations('onboarding');
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const accessToken = useAuthStore((state) => state.accessToken);
  const profile = useUserProfileStore((state) => state.profile);
  const loadedUserId = useUserProfileStore((state) => state.loadedUserId);
  const fetchProfile = useUserProfileStore((state) => state.fetchProfile);
  const userId = useAuthStore((state) => state.user?.id ?? null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);
  const shouldCheck = Boolean(
    isAuthenticated
    && accessToken
    && (pathname === '/onboarding' || isOnboardingProductRoute(pathname)),
  );
  const currentSearch = useMemo(() => {
    const serialized = searchParams?.toString() || '';
    return serialized ? `?${serialized}` : '';
  }, [searchParams]);

  useEffect(() => {
    if (!shouldCheck) return;
    let active = true;
    setLoadFailed(false);
    void fetchProfile(retryNonce > 0).then((nextProfile) => {
      if (active) setLoadFailed(!nextProfile);
    }).catch(() => {
      if (active) setLoadFailed(true);
    });
    return () => { active = false; };
  }, [fetchProfile, shouldCheck, userId, retryNonce]);

  // Navigation uses the current route and current owner's authoritative data,
  // never the pathname captured before a slow request or an account switch.
  const hasCurrentProfile = Boolean(userId && loadedUserId === userId && profile?.user_id === userId);
  useEffect(() => {
    if (shouldCheck && hasCurrentProfile && pathname !== '/onboarding' && isOnboardingRequired(profile)) {
      router.replace(buildOnboardingRedirect(pathname, currentSearch));
    }
  }, [shouldCheck, hasCurrentProfile, profile, pathname, currentSearch, router]);

  // Public browsing must stay mounted as the cookie session becomes available.
  // Otherwise recovery removes the clicked navigation while router.push is pending.
  // Keep the same Fragment slots in SSR, hydration and the ready state: changing
  // their shape also changes descendant useId values during selective hydration.
  if (isPublicRoutePath(pathname)
    && !(shouldCheck && hasCurrentProfile && isOnboardingRequired(profile))) {
    return <>
      {children}
      {shouldCheck && loadFailed && !hasCurrentProfile && <div role="status" className="fixed bottom-4 right-4 z-40 flex max-w-[calc(100vw-2rem)] items-center gap-3 rounded-2xl border border-[#DCE5EB] bg-white px-4 py-3 text-sm text-[#203947] shadow-sm">
        <span>{t('gate.loadFailedTitle')}</span>
        <StudioAction type="button" onClick={() => setRetryNonce((value) => value + 1)}>
          {t('actions.retry')}
        </StudioAction>
      </div>}
    </>;
  }

  if (!shouldCheck) {
    return <>{children}</>;
  }

  // 同一账号已确认的资料可继续展示；路由变化的后台检查不应卸载工作台。
  if (loadFailed && !hasCurrentProfile) {
    return (
      <div className={recovery.page}>
        <div className={recovery.card}>
          <CreationGlassBackdrop />
          <h1 className={`${recovery.title} text-xl tracking-[-0.02em]`}>
            {t('gate.loadFailedTitle')}
          </h1>
          <p className={`${recovery.body} mt-2 text-sm leading-6`}>{t('gate.loadFailedBody')}</p>
          <StudioAction
            type="button"
            onClick={() => setRetryNonce((value) => value + 1)}
            className={`${recovery.action} mt-6`}
          >
            {t('actions.retry')}
          </StudioAction>
        </div>
      </div>
    );
  }

  if (!hasCurrentProfile) {
    return <GuideOrbLoading label={t('gate.loading')} />;
  }

  if (pathname !== '/onboarding' && isOnboardingRequired(profile)) {
    return <GuideOrbLoading label={t('gate.redirecting')} />;
  }

  return <>{children}</>;
}
